import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { scoreToPercent } from '../../data/axisDefs';

/**
 * BipolarSlider — 5軸の両極スライダー表示
 *
 * <BipolarSlider
 *   label="行動特性"
 *   leftPole="応答"
 *   rightPole="自発"
 *   score={0.77}            // 0-1, 右極寄りの度合い
 *   color="#F4B860"
 *   showPercent
 *   lang="ja"
 * />
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: ${p => p.$compact ? '6px 0' : '10px 0'};
`;

const HeadRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const Label = styled.span`
  font-family: ${p => p.theme.fonts.body};
  font-size: ${p => p.$compact ? '0.85rem' : '0.95rem'};
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  letter-spacing: 0.04em;
`;

const SliderRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
`;

const Pole = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  white-space: nowrap;
  min-width: 2.8em;
  align-items: ${p => p.$align === 'right' ? 'flex-end' : 'flex-start'};
`;

const PoleName = styled.span`
  font-family: ${p => p.theme.fonts.body};
  font-size: 0.82rem;
  color: ${p => p.$active ? p.theme.colors.text : p.theme.colors.textMuted};
  letter-spacing: 0.02em;
`;

const PolePercent = styled.span`
  font-family: ${p => p.theme.fonts.mono};
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.03em;
  color: ${p => p.$active ? p.$color || p.theme.colors.primary : p.theme.colors.textLight};
`;

const Track = styled.div`
  position: relative;
  height: 2px;
  background: linear-gradient(
    90deg,
    rgba(241,236,223,0.18) 0%,
    rgba(241,236,223,0.32) 50%,
    rgba(241,236,223,0.18) 100%
  );
  border-radius: 999px;
`;

const Knob = styled(motion.div)`
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${p => p.$color};
  border: 1px solid rgba(255,255,255,0.25);
  box-shadow:
    0 0 12px ${p => p.$color}66,
    0 0 24px ${p => p.$color}33;
  transform: translate(-50%, -50%);
`;

const TickMark = styled.span`
  position: absolute;
  top: 50%;
  width: 1px;
  height: 6px;
  background: rgba(241,236,223,0.18);
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const BipolarSlider = ({
  label,
  leftPole,
  rightPole,
  score,
  color = '#E6C77E',
  showPercent = true,
  showCenterTick = true,
  compact = false,
  animate = true,
}) => {
  const safe = typeof score === 'number' && !isNaN(score)
    ? Math.max(0, Math.min(1, score))
    : null;
  // 右極寄りの % (= percent) と、その対になる左極寄りの % (= 100 - percent)
  const rightPercent = safe !== null ? scoreToPercent(safe) : null;
  const leftPercent  = rightPercent !== null ? 100 - rightPercent : null;
  const knobX = safe !== null ? `${safe * 100}%` : '50%';

  const leftActive  = safe !== null && safe < 0.5;
  const rightActive = safe !== null && safe > 0.5;

  return (
    <Wrap $compact={compact}>
      <HeadRow>
        <Label $compact={compact}>{label}</Label>
      </HeadRow>
      <SliderRow>
        <Pole $align="left">
          <PoleName $active={leftActive}>{leftPole}</PoleName>
          {showPercent && leftPercent !== null && (
            <PolePercent $active={leftActive} $color={color}>{leftPercent}%</PolePercent>
          )}
        </Pole>
        <Track>
          {showCenterTick && <TickMark style={{ left: '50%' }} />}
          {safe !== null && (
            <Knob
              $color={color}
              initial={animate ? { left: '50%', opacity: 0 } : { left: knobX, opacity: 1 }}
              animate={{ left: knobX, opacity: 1 }}
              transition={{ duration: animate ? 0.9 : 0, ease: [0.22, 1, 0.36, 1] }}
            />
          )}
        </Track>
        <Pole $align="right">
          <PoleName $active={rightActive}>{rightPole}</PoleName>
          {showPercent && rightPercent !== null && (
            <PolePercent $active={rightActive} $color={color}>{rightPercent}%</PolePercent>
          )}
        </Pole>
      </SliderRow>
    </Wrap>
  );
};

export default BipolarSlider;
